import { IInjectable, Ng2StateDeclaration, ResolveTypes, Transition } from '@uirouter/angular';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { UserService } from '@fi-app/user/user.service';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { DataFieldService } from '@fi-app/data-field/data-field.service';
import { PageTitleService } from '@fi-app/utilities/fi-services/page-title.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { UserEditNewLayoutComponent } from '@fi-app/user/user-edit/user-edit-new-layout/user-edit-new-layout.component';
import { BusinessAreaService } from '@fi-app/business-area/business-area.service';
import { FiCoreAddUserStateResolve_lightWeightBusinessAreas } from '@fi-app/user/states/user-add-state';
import {
    FiCoreUnitResolve_lightWeightBusinessUnits,
    FiCoreUserUnitResolve_accessRightFilterCollection,
} from '@fi-app/business-unit/states/resolve/access-right';
import { FiAuthenticateSharedService } from '@fi-shared/fi-auth-shared/fi-authenticate-shared.service';
import { UserModel } from '@fi-app/user/user.model';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';

export const FiCoreEditUserStateResolveTypes: ResolveTypes[] | { [p: string]: IInjectable } = {
    user: [FiStateService, UserService, Transition, FiCoreEditUserStateResolve_user],
    availableBusinessUnits: FiCoreUnitResolve_lightWeightBusinessUnits,
    lightWeightBusinessUnits: ['user', 'availableBusinessUnits', FiCoreEditUserStateResolve_lightWeightBusinessUnits],
    lightWeightBusinessAreas: [
        'lightWeightBusinessUnits',
        BusinessAreaService,
        FiCoreAddUserStateResolve_lightWeightBusinessAreas,
    ],
    contentLanguages: [ContentLanguagesService, FiCoreEditUserStateResolve_contentLanguages],
    applicationLanguages: [ContentLanguagesService, FiCoreEditUserStateResolve_applicationLanguages],
    accessRightFilterCollection: FiCoreUserUnitResolve_accessRightFilterCollection,
    userDataFields: ['user', DataFieldService, FiCoreEditUserStateResolve_userDataFields],
    userEmailUniqueData: [
        'user',
        FiAuthenticateSharedService,
        FiContactsService,
        (
            user: UserModel,
            authenticateSharedService: FiAuthenticateSharedService,
            fiContactsService: FiContactsService,
        ) => {
            const contacts = user.EmailAddressContacts.concat(user.PhoneNumberContacts);
            const contactValues = contacts.map((c) => fiContactsService.getFullContactValue(c));
            return authenticateSharedService.getContactUniqueData(user.Id, contactValues);
        },
    ],
    pageTitle: [PageTitleService, 'user', FiCoreEditUserStateResolve_pageTitle],
};

export const FiCoreEditUserStateV2: Ng2StateDeclaration = {
    name: FiStateNames.User.EditV2,
    url: '/:Id/EditV2',
    params: {
        isAccessRequesting: null, //determines if user edited from requests tab
    },
    views: {
        '@': {
            component: UserEditNewLayoutComponent,
        },
    },
    resolve: FiCoreEditUserStateResolveTypes,
};

export function FiCoreEditUserStateResolve_user(
    fiStateService: FiStateService,
    userService: UserService,
    transition: Transition,
) {
    const userId = coerceNumberProperty(fiStateService.getParams(transition).Id);
    const isAccessRequesting = coerceBooleanProperty(fiStateService.getParams(transition).isAccessRequesting);
    return userService.getUserForEdit(userId, isAccessRequesting);
}

/*
export function FiCoreEditUserStateResolve_availableBusinessUnits(accessRightService: AccessRightService) {
    return accessRightService.GetBusinessUnitsForUserProfile();
}
*/

export function FiCoreEditUserStateResolve_lightWeightBusinessUnits(user, availableBusinessUnits) {
    const data = availableBusinessUnits.slice();
    user.BusinessUnits.forEach((businessUnit) => {
        if (!data.find((item) => item.Id === businessUnit.Id)) {
            data.push(businessUnit);
        }
    });
    return data;
}

export function FiCoreEditUserStateResolve_contentLanguages(contentLanguagesService: ContentLanguagesService) {
    return contentLanguagesService.GetContentLanguages();
}

export function FiCoreEditUserStateResolve_applicationLanguages(contentLanguagesService: ContentLanguagesService) {
    return contentLanguagesService.GetApplicationLanguages();
}

export function FiCoreEditUserStateResolve_userDataFields(user, dataFieldService: DataFieldService) {
    return dataFieldService.getUserFields(user.Id);
}

export function FiCoreEditUserStateResolve_pageTitle(pageTitleService: PageTitleService, user) {
    return pageTitleService.setTitleByItem(user);
}
